// Shared helpers for Chart.js bar charts:
// - external tooltip styled like Intercom reports
// - hover dimming by dataset (keep same color bright, dim others)

const toNumber = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const withAlpha = (hex, alpha) => {
  const raw = String(hex || '')
    .replace('#', '')
    .trim();
  if (raw.length !== 6) return hex;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ensureTooltipEl = id => {
  const existing = document.getElementById(id);
  if (existing) return existing;
  const el = document.createElement('div');
  el.id = id;
  el.style.cssText =
    'opacity:0;position:fixed;pointer-events:none;transform:translate(-50%,-100%);transition:opacity 120ms ease;z-index:9999;';
  document.body.appendChild(el);
  return el;
};

export const removeTooltipEl = id => {
  const el = document.getElementById(id);
  if (el?.cwHideTimer) {
    window.clearTimeout(el.cwHideTimer);
    el.cwHideTimer = null;
  }
  if (el?.parentNode) el.parentNode.removeChild(el);
};

const clearTooltipHideTimer = el => {
  if (!el?.cwHideTimer) return;
  window.clearTimeout(el.cwHideTimer);
  el.cwHideTimer = null;
};

const hideTooltipEl = el => {
  clearTooltipHideTimer(el);
  if (el) el.style.opacity = '0';
};

const scheduleTooltipHide = (el, delay = 160) => {
  clearTooltipHideTimer(el);
  el.cwHideTimer = window.setTimeout(() => {
    if (el.matches(':hover') || el.contains(document.activeElement)) return;
    el.style.opacity = '0';
  }, delay);
};

const getBaseDatasetColor = dataset => {
  const bg = dataset?.backgroundColor;
  if (Array.isArray(bg)) return bg[0];
  return bg;
};

const storeBaseColorsIfMissing = chart => {
  const datasets = chart?.data?.datasets;
  if (!Array.isArray(datasets)) return;
  datasets.forEach(ds => {
    if (!ds) return;
    if (ds.cwBaseBgColor) return;
    ds.cwBaseBgColor = getBaseDatasetColor(ds);
  });
};

export const applyDatasetDimming = (
  chart,
  hoveredDatasetIndex,
  alpha = 0.25
) => {
  if (!chart?.data?.datasets) return;
  storeBaseColorsIfMissing(chart);

  const datasets = chart.data.datasets;
  datasets.forEach((dataset, idx) => {
    const base = dataset?.cwBaseBgColor ?? getBaseDatasetColor(dataset);
    if (!base) return;
    if (hoveredDatasetIndex == null) {
      dataset.backgroundColor = base;
      return;
    }
    dataset.backgroundColor =
      idx === hoveredDatasetIndex ? base : withAlpha(base, alpha);
  });

  chart.update('none');
};

export const buildIntercomLikeBarTooltip =
  ({
    tooltipId,
    titleForIndex,
    seriesLabelForDataset,
    valueFormatter = v => toNumber(v).toLocaleString(),
    percentDigits = 0,
    showPercent = true,
    actionLabelForIndex,
    onActionClick,
  }) =>
  context => {
    const { chart, tooltip } = context;
    const el = ensureTooltipEl(tooltipId);
    const isInteractive =
      typeof actionLabelForIndex === 'function' &&
      typeof onActionClick === 'function';

    if (!tooltip || tooltip.opacity === 0) {
      if (isInteractive) {
        scheduleTooltipHide(el);
        return;
      }

      hideTooltipEl(el);
      return;
    }

    const dataPoint = tooltip.dataPoints?.[0];
    const dataIndex = dataPoint?.dataIndex;
    const datasetIndex = dataPoint?.datasetIndex;
    if (dataIndex == null || datasetIndex == null) return;

    const title =
      typeof titleForIndex === 'function'
        ? titleForIndex(dataIndex)
        : tooltip?.title?.[0] || '';
    const actionLabel = isInteractive
      ? String(actionLabelForIndex(dataIndex, datasetIndex) || '').trim()
      : '';

    const rawValue = toNumber(dataPoint?.raw);
    const datasets = chart?.data?.datasets || [];
    const totalAtIndex = datasets.reduce((sum, ds) => {
      const v = Array.isArray(ds?.data) ? ds.data[dataIndex] : 0;
      return sum + toNumber(v);
    }, 0);

    const percent =
      showPercent && totalAtIndex > 0
        ? ((rawValue / totalAtIndex) * 100).toFixed(percentDigits)
        : null;

    const seriesLabel =
      typeof seriesLabelForDataset === 'function'
        ? seriesLabelForDataset(datasetIndex)
        : String(dataPoint?.dataset?.label || '');

    const dotColor =
      tooltip?.labelColors?.[0]?.backgroundColor ||
      getBaseDatasetColor(datasets[datasetIndex]) ||
      '#cbd5e1';

    clearTooltipHideTimer(el);

    if (isInteractive && !el.cwInteractiveBound) {
      el.cwInteractiveBound = true;
      el.addEventListener('mouseenter', () => clearTooltipHideTimer(el));
      el.addEventListener('mouseleave', () => hideTooltipEl(el));
      el.addEventListener('click', event => {
        const actionButton = event.target.closest(
          '[data-cw-tooltip-action="true"]'
        );
        if (!actionButton) return;

        event.preventDefault();
        onActionClick({
          dataIndex: Number(el.dataset.cwDataIndex || -1),
          datasetIndex: Number(el.dataset.cwDatasetIndex || -1),
          rawValue: toNumber(el.dataset.cwRawValue),
        });
        hideTooltipEl(el);
      });
    }

    el.style.pointerEvents = isInteractive ? 'auto' : 'none';
    el.dataset.cwDataIndex = String(dataIndex);
    el.dataset.cwDatasetIndex = String(datasetIndex);
    el.dataset.cwRawValue = String(rawValue);

    el.innerHTML = `
    <div style="min-width:220px;border:1px solid #d1d5db;background:#fff;border-radius:4px;box-shadow:rgba(0,0,0,0.2) 0 5px 10px 0;overflow:hidden;">
      <div style="background:#eff3f5;font-size:13px;line-height:20px;color:#111827;padding:4px 10px;text-align:center;">${title}</div>
      <div style="font-size:13px;line-height:20px;color:#424d57;padding:10px 12px;background:#fff;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="width:10px;height:10px;border-radius:3px;background:${dotColor};display:inline-block;"></span>
          <span>
            <strong style="font-weight:600;color:#111827;">${valueFormatter(rawValue)}</strong>
            <span style="margin-left:4px;">${seriesLabel}</span>
            ${
              percent != null
                ? `<span style="color:#6b7280;margin-left:6px;">(${percent}%)</span>`
                : ''
            }
          </span>
        </div>
        ${
          actionLabel
            ? `<div style="margin-top:10px;display:flex;justify-content:flex-end;">
                <button
                  type="button"
                  data-cw-tooltip-action="true"
                  style="border:1px solid #d1d5db;background:#fff;color:#111827;border-radius:8px;padding:6px 10px;font-size:12px;font-weight:600;cursor:pointer;"
                >
                  ${actionLabel}
                </button>
              </div>`
            : ''
        }
      </div>
    </div>
  `;

    const rect = chart.canvas.getBoundingClientRect();
    const tooltipWidth = el.offsetWidth || 220;
    const tooltipHeight = el.offsetHeight || 80;
    const left = clamp(
      rect.left + tooltip.caretX,
      tooltipWidth / 2 + 8,
      window.innerWidth - tooltipWidth / 2 - 8
    );
    const top = Math.max(rect.top + tooltip.caretY - 10, tooltipHeight + 8);

    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.opacity = '1';
  };

export const buildDatasetHoverDimmingHandler =
  ({ hoveredDatasetIndexRef, alpha = 0.25 } = {}) =>
  (_event, active, chart) => {
    const hoveredDatasetIndex = active?.length ? active[0].datasetIndex : null;
    if (hoveredDatasetIndexRef) {
      if (hoveredDatasetIndexRef.value === hoveredDatasetIndex) return;
      hoveredDatasetIndexRef.value = hoveredDatasetIndex;
    }
    applyDatasetDimming(chart, hoveredDatasetIndex, alpha);
  };
